<template>
  <div
    class="sponsor-info-modal"
    role="dialog"
    aria-modal="true"
    :aria-label="$t('Components.SponsorInfoModal.sponsorDetails')"
  >
    <button
      class="sponsor-info-modal__close"
      @click="onClose"
      :aria-label="$t('Components.SponsorInfoModal.close')"
    >
      <i class="fas fa-times"></i>
    </button>

    <div class="sponsor-info-modal__header">
      <div class="sponsor-info-modal__logo-wrap">
        <img
          v-if="sponsor && sponsor.logoUrl"
          :src="sponsor.logoUrl"
          :alt="sponsor.name || $t('Components.SponsorInfoModal.sponsorLogo')"
          class="sponsor-info-modal__logo"
        />
        <div v-else class="sponsor-info-modal__logo-placeholder">
          <i class="far fa-building"></i>
        </div>
      </div>
      <h2 class="sponsor-info-modal__name">{{ sponsor && sponsor.name }}</h2>
      <div
        v-if="sponsor && sponsor.tagline"
        class="sponsor-info-modal__tagline"
      >
        {{ sponsor.tagline }}
      </div>
    </div>

    <div
      v-if="sponsor && (sponsor.description || sponsor.about)"
      class="sponsor-info-modal__description html-content"
      v-html="sponsor.description || sponsor.about"
    />

    <div class="sponsor-info-modal__details">
      <div v-if="sponsor && sponsor.address" class="detail-row">
        <div class="detail-row__icon detail-row__icon--orange">
          <i class="fas fa-map-marker-alt"></i>
        </div>
        <span class="detail-row__text">{{ sponsor.address }}</span>
      </div>

      <div v-if="sponsor && sponsor.phoneNumber" class="detail-row">
        <div class="detail-row__icon detail-row__icon--green">
          <i class="fas fa-phone"></i>
        </div>
        <a :href="'tel:' + sponsor.phoneNumber" class="detail-row__link">
          {{ sponsor.phoneNumber }}
        </a>
      </div>

      <div v-if="sponsor && sponsor.email" class="detail-row">
        <div class="detail-row__icon detail-row__icon--teal">
          <i class="far fa-envelope"></i>
        </div>
        <a :href="'mailto:' + sponsor.email" class="detail-row__link">
          {{ sponsor.email }}
        </a>
      </div>

      <div v-if="sponsor && sponsor.website" class="detail-row">
        <div class="detail-row__icon detail-row__icon--purple">
          <i class="fas fa-globe"></i>
        </div>
        <a
          :href="normalizeUrl(sponsor.website)"
          target="_blank"
          rel="noopener"
          class="detail-row__link"
        >
          {{ sponsor.website }}
        </a>
      </div>
    </div>

    <div class="sponsor-info-modal__footer">
      <button
        v-if="sponsor && sponsor.id"
        class="btn-visit"
        @click="goToSponsorPage"
      >
        {{ $t('Components.SponsorInfoModal.visitSponsorPage') }}
      </button>
      <button class="btn-close-secondary" @click="onClose">
        {{ $t('Components.SponsorInfoModal.close') }}
      </button>
    </div>
  </div>
</template>

<script>
import focusTrapMixin from '@/mixins/focusTrap';

export default {
  name: 'SponsorInfoModal',
  mixins: [focusTrapMixin],
  props: {
    sponsor: { type: Object, default: null }
  },
  mounted() {
    this.activateFocusTrap();
  },
  beforeDestroy() {
    this.deactivateFocusTrap();
    // Outside click and Esc close the modal without going through
    // onClose, so release the page scroll lock here as well.
    document.body.classList.remove('overflow-hidden');
  },
  methods: {
    onClose() {
      document.body.classList.remove('overflow-hidden');
      this.$emit('close');
    },
    normalizeUrl(url) {
      if (!url) return url;
      const trimmed = String(url).trim();
      if (!/^https?:\/\//i.test(trimmed)) return `https://${trimmed}`;
      return trimmed;
    },
    goToSponsorPage() {
      const id = this.sponsor && (this.sponsor.id || this.sponsor.Id);
      if (!id) return;
      // Close the modal first, then navigate on the next tick so the
      // close event is processed before the route changes — otherwise
      // the popup stays mounted over the sponsor page.
      this.onClose();
      this.$nextTick(() => {
        this.$router
          .push({ name: 'sponsor', params: { sponsorId: id } })
          .catch(() => {});
      });
    }
  }
};
</script>

<style scoped lang="scss">
.sponsor-info-modal {
  position: relative;
  background: #ffffff;
  border-radius: 16px;
  padding: 32px 28px 24px;
  font-family: 'Poppins', sans-serif;
  max-height: 90vh;
  overflow-y: auto;

  &__close {
    position: absolute;
    top: 16px;
    right: 16px;
    width: 32px;
    height: 32px;
    border-radius: 50%;
    border: none;
    background: #f3f4f6;
    color: #374151;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    transition: background 0.15s;

    &:hover {
      background: #e5e7eb;
    }
  }

  &__header {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-bottom: 20px;
    padding-right: 32px;
  }

  &__logo-wrap {
    width: 110px;
    height: 110px;
    border-radius: 16px;
    background: #f9fafb;
    border: 1px solid #e5e7eb;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 14px;
    padding: 12px;
    overflow: hidden;
  }

  &__logo {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }

  &__logo-placeholder {
    font-size: 36px;
    color: #d1d5db;
  }

  &__name {
    font-size: 22px;
    font-weight: 700;
    color: #111827;
    margin: 0 0 4px;
    line-height: 1.25;
  }

  &__tagline {
    font-size: 14px;
    color: #6b7280;
  }

  &__description {
    font-size: 14px;
    color: #374151;
    line-height: 1.65;
    border-top: 1px solid #f3f4f6;
    padding-top: 18px;
    margin-bottom: 20px;
  }

  &__details {
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 24px;
  }

  &__footer {
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
    justify-content: flex-end;
    border-top: 1px solid #f3f4f6;
    padding-top: 18px;
  }
}

.detail-row {
  display: flex;
  align-items: center;
  gap: 14px;

  &__icon {
    width: 40px;
    height: 40px;
    border-radius: 10px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 14px;
    flex-shrink: 0;

    &--teal {
      background: #e6f7f5;
      color: var(--color-primary, #047873);
    }
    &--purple {
      background: #f3f0ff;
      color: #7c3aed;
    }
    &--orange {
      background: #fff4ed;
      color: #ea580c;
    }
    &--green {
      background: #ecfdf5;
      color: #16a34a;
    }
  }

  &__text {
    font-size: 14px;
    color: #374151;
    word-break: break-word;
  }

  &__link {
    font-size: 14px;
    color: var(--color-primary, #047873);
    text-decoration: none;
    word-break: break-word;

    &:hover {
      text-decoration: underline;
    }
  }
}

.btn-visit {
  min-height: 40px;
  padding: 12px 24px;
  background: var(--color-primary, #047873);
  color: #ffffff;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  transition: background 0.15s;

  &:hover {
    background: var(--color-primary-hover, #036663);
  }
}

.btn-close-secondary {
  padding: 10px 20px;
  background: #ffffff;
  color: #374151;
  border: 1.5px solid #e5e7eb;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  font-family: 'Poppins', sans-serif;
  transition: border-color 0.15s;

  &:hover {
    border-color: var(--color-primary, #047873);
  }
}

::v-deep .html-content {
  p {
    margin-bottom: 10px;
  }
  h1,
  h2,
  h3 {
    font-weight: 700;
    margin-bottom: 8px;
  }
  ul,
  ol {
    padding-left: 20px;
    margin-bottom: 10px;
  }
  a {
    color: var(--color-primary, #047873);
  }
}
</style>
